import React from "react";
import { Box, Chip, Label } from "@telia/teddy";
import { RDSubscription } from "../../../../../../models";
import { CustomerNewSubscriptionIcim } from "../../../../../../models/state";
import "./GroupSelect.less";

export interface GroupSelectProps {
    name: string;
    icimGroups: CustomerNewSubscriptionIcim[];
    selectedGroup: string;
    selectedPricePlan: string | undefined;
    selectableSubscriptions: RDSubscription[];
    selectedOfferingCode: string | undefined;
    findFallbackSub?: (subs: RDSubscription[]) => RDSubscription | undefined;
    onGroupChange: (value: string, firstOfferingCode: string | undefined) => void;
}

const GroupSelect: React.FC<GroupSelectProps> = ({
    name,
    icimGroups,
    selectedGroup,
    selectedPricePlan,
    selectableSubscriptions,
    selectedOfferingCode,
    findFallbackSub,
    onGroupChange,
}) => {
    if (!selectedPricePlan || icimGroups.length <= 1) return null;

    const isSelectedInSubscriptionSelect = selectableSubscriptions.some(
        (s) => `${s.offeringCode}.${s.icim}` === selectedOfferingCode,
    );
    if (!isSelectedInSubscriptionSelect) return null;

    const handleValueChange = (value: string) => {
        if (!value) return;

        const newGroup = icimGroups.find((g) => g.group === value);
        if (!newGroup) return;

        const matchedSub = newGroup.subscriptions.find((s) => s.offeringCode === selectedPricePlan);
        const fallback = findFallbackSub ? findFallbackSub(newGroup.subscriptions) : newGroup.subscriptions[0];
        const firstSub = matchedSub ?? fallback;
        onGroupChange(value, firstSub ? `${firstSub.offeringCode}.${firstSub.icim}` : undefined);
    };

    const isGroupAvailable = (group: CustomerNewSubscriptionIcim) =>
        group.subscriptions.some((s) => s.offeringCode === selectedPricePlan);

    const labelId = `${name}-label`;

    return (
        <Box mt="200" className="group-select">
            <Label id={labelId} className="group-select__label">
                Velg avtale/bindingsperiode
            </Label>
            <Chip
                className="group-select__chip"
                mt="50"
                mb="400"
                type="single"
                variant="choice"
                value={selectedGroup}
                onValueChange={handleValueChange}
                aria-labelledby={labelId}
            >
                {icimGroups
                    .filter((icim) => icim.subscriptions.length > 0)
                    .map((icim) => (
                        <Chip.Item
                            key={icim.group}
                            className="group-select__chip-item"
                            value={icim.group}
                            disabled={!isGroupAvailable(icim)}
                        >
                            <span className="group-select__chip-content">
                                <span>{icim.label}</span>
                                {icim.description && (
                                    <span className="group-select__chip-description">{icim.description}</span>
                                )}
                            </span>
                        </Chip.Item>
                    ))}
            </Chip>
        </Box>
    );
};

export default GroupSelect;
